import {
  Projectile,
  Circle,
  Crosshair,
  Drawable,
  Moveable,
  CanvasItems,
} from './lib/objects'
import { Slope, Coordinate, Vector2d } from './lib/coordinates'
import { getRandomColor } from './lib/util'

const KEYCODE_SHIFT = 16;
const KEYCODE_ESC = 27;

// Get HTML elements and data
let statusDiv: HTMLElement
let ballDiv: HTMLElement

// Set globals
const projectileSpeed = 4
const projectileSize = 10
let ceaseFire = true
let contained = false

// Setup initial markers
let canvasCenter: Coordinate
const mouseMarker = new Crosshair(new Coordinate(0, 0), 5, 2, 'blue')

const canvasItems = new CanvasItems(
  new Set([] as Array<Drawable>),
  new Set([] as Array<Moveable>),
  new Set([] as Array<Drawable>),
)

const ballMap: Map<Moveable, any> = new Map()
const ballElementMap: Map<any, Moveable> = new Map()

const followMouse = (event: any) =>
  mouseMarker.setNewLocation(new Coordinate(event.offsetX, event.offsetY))

function fireAtMouse(e: any) {
  if (!ceaseFire) addProjectileToCanvas()
}

export const handleKeyUp = (event: any) => {
  if (event.keyCode === KEYCODE_ESC) clearAll()
  if (event.keyCode === KEYCODE_SHIFT) toggleContainer()
}

export const handleMouseEnterWithCanvas = (
  gameCanvas: HTMLCanvasElement,
) => () => {
  ceaseFire = false
  canvasItems.drawables.add(mouseMarker)
  gameCanvas.addEventListener('mousemove', followMouse)
  gameCanvas.addEventListener('mousedown', fireAtMouse)
}

export const handleMouseLeaveWithCanvas = (
  gameCanvas: HTMLCanvasElement,
) => () => {
  ceaseFire = true
  canvasItems.drawables.delete(mouseMarker)
  gameCanvas.removeEventListener('mousemove', followMouse)
  gameCanvas.removeEventListener('mousedown', fireAtMouse)
}

export const initialize = (
  gameCanvas: HTMLCanvasElement,
  statusDivInit: HTMLElement,
  ballDivInit: HTMLElement,
) => {
  canvasCenter = new Coordinate(gameCanvas.width / 2, gameCanvas.height / 2)
  const canvasCenterMarker = new Circle(canvasCenter, 2)

  canvasItems.drawables.add(canvasCenterMarker)
  statusDiv = statusDivInit
  ballDiv = ballDivInit
}

export function render(gameCanvas: HTMLCanvasElement, ctx: any) {
  if (statusDiv)
    statusDiv.innerText = `# of Projectiles: ${canvasItems.moveables.size}`
  ctx.clearRect(0, 0, gameCanvas.width, gameCanvas.height)
  if (contained) drawBoundingRect(gameCanvas, ctx)
  canvasItems.drawables.forEach((item) => {
    if (!item) return
    item.draw(ctx)
  })
  canvasItems.moveables.forEach((item) => {
    if (!item) return
    item.draw(ctx)
    if (canvasItems.detail === item) item.drawDetail(ctx)
    if (item.move) item.move()
    if (exited(item, gameCanvas)) {
      const element = ballMap.get(item)
      if (ballDiv) ballDiv.removeChild(element)
      ballMap.delete(item)
      ballElementMap.delete(element)
      canvasItems.moveables.delete(item)
    }
    if (item.contained) keepInside(item, gameCanvas)
  })
  canvasItems.moveables.forEach((c1, index) => {
    const iter = canvasItems.moveables.values()
    let c2 = iter.next().value
    while (c1 !== c2) {
      c2 = iter.next().value
    }
    while (c2) {
      c2 = iter.next().value
      if (
        c1 instanceof Projectile &&
        c2 instanceof Projectile &&
        checkAABBCollision(c1, c2) &&
        checkFineCollision(c1, c2)
      ) {
        calculatePointOfCollision_Circles(c1, c2)
        processCollision(c1, c2)
        // TODO: Do some animation at calculated collision point
      }
    }
  })
}

export function renderDetail(detailCanvas: HTMLCanvasElement, ctx: any) {
  ctx.clearRect(0, 0, detailCanvas.width, detailCanvas.height)
  if (!canvasItems.detail) return
  const ball = canvasItems.detail
  ctx.beginPath()
  ctx.arc(100, 100, 26, 0, Math.PI * 2, false)
  ctx.fillStyle = ball.fill
  ctx.fill()
  ctx.closePath()

  const scaled = ball.vector.scale(12)
  const x = 100 + scaled.dx
  const y = 100 + scaled.dy

  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(100, 100)
  ctx.lineTo(x, 100)
  ctx.strokeStyle = 'blue'
  ctx.stroke()
  ctx.closePath()

  ctx.beginPath()
  ctx.moveTo(100, 100)
  ctx.lineTo(100, y)
  ctx.strokeStyle = 'green'
  ctx.stroke()
  ctx.closePath()

  ctx.beginPath()
  ctx.moveTo(100, 100)
  ctx.lineTo(x, y)
  ctx.strokeStyle = 'black'
  ctx.stroke()
  ctx.closePath()
  ctx.lineWidth = 1

  ctx.fillText(`dx: ${ball.vector.dx}`, 250, 40)
  ctx.fillText(`dy: ${ball.vector.dy}`, 250, 60)
  ctx.fillText(`magnitude: ${ball.vector.magnitude}`, 250, 80)
}

function checkAABBCollision(r1: Moveable, r2: Moveable): boolean {
  return !(
    r1.aabb[0].x > r2.aabb[1].x ||
    r1.aabb[0].y > r2.aabb[1].y ||
    r1.aabb[1].x < r2.aabb[0].x ||
    r1.aabb[1].y < r2.aabb[0].y
  )
}

function checkFineCollision(c1: Projectile, c2: Projectile): boolean {
  const distance = c1.location.distanceTo(c2.location)
  return distance <= c1.radius + c2.radius
}

function calculatePointOfCollision_Circles(
  c1: Projectile,
  c2: Projectile,
): Coordinate {
  const x =
    (c1.location.x * c2.radius + c2.location.x * c1.radius) /
    (c1.radius + c2.radius)
  const y =
    (c1.location.y * c2.radius + c2.location.y * c1.radius) /
    (c1.radius + c2.radius)
  return new Coordinate(x, y)
}

function processCollision(c1: Projectile, c2: Projectile) {
  const collisionVector = new Vector2d(
    Slope.getSlopeFromCoordinates(c1.location, c2.location),
  ).getUnitVector()
  const a1 = c1.vector.dot(collisionVector)
  const a2 = c2.vector.dot(collisionVector)

  const optimizedP = a1 - a2

  const newV1 = c1.vector.subtract(collisionVector.scale(optimizedP))
  const newV2 = c2.vector.add(collisionVector.scale(optimizedP))

  c1.setNewVector(newV1)
  c2.setNewVector(newV2)

  let pt1: Coordinate
  let pt2: Coordinate

  do {
    pt1 = c1.vector.getNextPoint(c1.location)
    pt2 = c2.vector.getNextPoint(c2.location)

    c1.move()
    c2.move()
  } while (Coordinate.calculateDistance(pt1, pt2) < c1.radius + c2.radius)
}

function addProjectileToCanvas() {
  console.log(mouseMarker.location)
  const projectile = new Projectile(
    canvasCenter,
    mouseMarker.location,
    projectileSpeed,
    projectileSize,
    contained,
    getRandomColor()
  )
  console.log(projectile)
  canvasItems.moveables.add(projectile)

  const ballE = document.createElement('div')
  ballE.className = 'ball'
  ballE.setAttribute('style', `background-color:${projectile.fill};`)
  ballE.addEventListener('mousedown', ballStartHover)
  if (ballDiv) ballDiv.appendChild(ballE)
  ballMap.set(projectile, ballE)
  ballElementMap.set(ballE, projectile)
}

function ballStartHover(e: any) {
  ballElementMap.forEach((val, key) => {
    key.className = 'ball'
  })
  e.target.className = 'ball hover'
  const projectile = ballElementMap.get(e.target)
  canvasItems.detail = projectile as Projectile
}

function keepInside(item: Moveable, canvasElement: any) {
  let canvasTop = new Coordinate(item.location.x, 0)
  let canvasBottom = new Coordinate(item.location.x, canvasElement.height)
  let canvasLeft = new Coordinate(0, item.location.y)
  let canvasRight = new Coordinate(canvasElement.width, item.location.y)

  if (item.containsPoint(canvasTop) || item.containsPoint(canvasBottom)) {
    item.reverseDy()
    item.move()
  }
  if (item.containsPoint(canvasLeft) || item.containsPoint(canvasRight)) {
    item.reverseDx()
    item.move()
  }
}

function exited(item: Moveable, canvasElement: any): boolean {
  return (
    item.location.x < 0 ||
    item.location.x > canvasElement.width ||
    item.location.y < 0 ||
    item.location.y > canvasElement.height
  )
}

function clearAll() {
  ballMap.forEach((ballElement) => {
    ballDiv && ballDiv.removeChild(ballElement)
  })
  ballMap.clear()
  ballElementMap.clear()
  canvasItems.moveables.clear()
  canvasItems.animations.clear()
  canvasItems.detail = null
  statusDiv.innerText = `# of Projectiles: ${canvasItems.moveables.size}`
}

function toggleContainer() {
  contained = !contained
  canvasItems.moveables.forEach((item) => {
    item.contained = contained
  })
}

function drawBoundingRect(gameCanvas: HTMLCanvasElement, ctx: any) {
  ctx.beginPath()
  ctx.rect(0, 0, gameCanvas.width, gameCanvas.height)
  ctx.strokeStyle = 'red'
  ctx.stroke()
  ctx.closePath()
}
